// Readout — the centralized per-system readout.
//
// One Overview tab (every system at a glance: narrative headline, live
// numbers, cost / benefit / net for the window) and one tab per system from
// the registry (the written readout, live stats, agent-recorded metrics and
// the cost / funding / benefit ledger). Data: GET /admin/readout*. Live
// numbers are computed on request; the narrative, metrics and ledger are what
// the readout curator agent recorded through /api/internal/readout.

import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { readoutService, type ReadoutOverview, type SystemCard } from '../../services/readout';
import { OverviewTab } from './OverviewTab';
import { SystemTab } from './SystemTab';
import { SourcesTab } from './SourcesTab';
import { formatStamp } from './readoutFormat';
import styles from './ReadoutPage.module.css';

export type ReadoutWindow = 7 | 30 | 90 | 365;

const WINDOWS: { key: ReadoutWindow; label: string }[] = [
  { key: 7, label: 'Last 7 days' },
  { key: 30, label: 'Last 30 days' },
  { key: 90, label: 'Last 90 days' },
  { key: 365, label: 'Last year' },
];

const OVERVIEW = 'overview';
const SOURCES = 'sources';

function parseWindow(raw: string | null): ReadoutWindow {
  const n = Number(raw);
  return WINDOWS.some((w) => w.key === n) ? (n as ReadoutWindow) : 30;
}

function freshnessClass(card: SystemCard, staleAfter: number): string {
  if (card.narrative_age_days == null) return styles.tabDotMissing;
  return card.narrative_age_days > staleAfter ? styles.tabDotStale : styles.tabDotOk;
}

export function ReadoutPage() {
  const { user, hasScope } = useAuth();
  const allowed = user?.role === 'ADMIN' || hasScope('exec:portal');

  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = searchParams.get('system') ?? OVERVIEW;
  const windowDays = parseWindow(searchParams.get('days'));

  // Request-key pattern (no setState in the effect body): `loading` is derived
  // from whether the latest result matches the current window; the previous
  // overview stays on screen while a new window loads.
  const requestKey = String(windowDays);
  const [result, setResult] = useState<{ key: string; overview: ReadoutOverview | null; error: string | null } | null>(null);
  const overview = result?.overview ?? null;
  const loading = allowed && result?.key !== requestKey;
  const error = result?.key === requestKey ? result.error : null;

  useEffect(() => {
    if (!allowed) return;
    let alive = true;
    readoutService
      .getOverview(windowDays)
      .then((o) => alive && setResult({ key: requestKey, overview: o, error: null }))
      .catch((err: unknown) =>
        alive &&
        setResult((prev) => ({
          key: requestKey,
          overview: prev?.overview ?? null,
          error: err instanceof Error ? err.message : 'Failed to load the readout',
        })),
      );
    return () => {
      alive = false;
    };
  }, [allowed, windowDays, requestKey]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value == null) next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const selectTab = (key: string) => setParam('system', key === OVERVIEW ? null : key);
  const selectWindow = (days: ReadoutWindow) => setParam('days', days === 30 ? null : String(days));

  const cards = useMemo(() => overview?.systems ?? [], [overview]);
  const activeCard = cards.find((c) => c.system.key === activeTab) ?? null;

  if (!allowed) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Readout</h1>
        <div className={styles.notice}>This readout is available to admins and executive readers only.</div>
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <div className={styles.topline}>
        <div>
          <h1 className={styles.title}>Readout</h1>
          <p className={styles.subtitle}>
            Every system we run, in one place: how it is doing, what it costs, what funds it, what it returns.
          </p>
          {overview && (
            <div className={styles.liveband}>
              <span className={styles.dotg} /> Live numbers as of {formatStamp(overview.as_of, true)} · narratives by the
              readout curator
            </div>
          )}
        </div>
        <div className={styles.seg} role="tablist" aria-label="Time window">
          {WINDOWS.map((w) => (
            <button
              key={w.key}
              type="button"
              className={windowDays === w.key ? styles.segOn : ''}
              aria-pressed={windowDays === w.key}
              onClick={() => selectWindow(w.key)}
            >
              {w.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.tabs} role="tablist" aria-label="Systems">
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === OVERVIEW}
          className={`${styles.tab} ${activeTab === OVERVIEW ? styles.tabActive : ''}`}
          onClick={() => selectTab(OVERVIEW)}
        >
          Overview
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={activeTab === SOURCES}
          className={`${styles.tab} ${activeTab === SOURCES ? styles.tabActive : ''}`}
          onClick={() => selectTab(SOURCES)}
          title="Where every number comes from and how it is calculated"
        >
          Sources &amp; calculations
        </button>
        {cards.map((card) => (
          <button
            key={card.system.key}
            type="button"
            role="tab"
            aria-selected={activeTab === card.system.key}
            className={`${styles.tab} ${activeTab === card.system.key ? styles.tabActive : ''}`}
            onClick={() => selectTab(card.system.key)}
            title={card.narrative_headline ?? 'No readout written yet'}
          >
            <span className={`${styles.tabDot} ${freshnessClass(card, overview?.stale_after_days ?? 14)}`} />
            {card.system.name}
          </button>
        ))}
      </div>

      {error && <div className={styles.error}>{error}</div>}
      {loading && !overview && <div className={styles.loading}>Loading the readout…</div>}

      {overview && activeTab === OVERVIEW && <OverviewTab overview={overview} onSelect={selectTab} />}
      {activeTab === SOURCES && <SourcesTab systems={cards.map((c) => c.system)} onSelect={selectTab} />}
      {overview && activeTab !== OVERVIEW && activeTab !== SOURCES && (
        <SystemTab systemKey={activeTab} windowDays={windowDays} card={activeCard} />
      )}
    </div>
  );
}

export default ReadoutPage;
